/* eslint-disable @typescript-eslint/camelcase */
import * as Constant from "@/constant";
import { ErrorCode } from "@/constant";
import { Network } from "@/network";
import { Log } from "@/util";

// ------------------------------ type ------------------------------

export type Banner = {
  bid: string;
  name: string;
  pic: string;
  type: string;
  url: string;
};

export type HomeMaterials = {
  themeTitle: string;
  downloadFilePath: string;
  list: {
    [key: string]: {
      id: string;
      src: string;
      url: string;
      title: string;
      userFavorite: boolean;
      userContact: boolean;
    }[];
  };
};

type HomeData = {
  bannerList: Banner[];
  materials: HomeMaterials;
};

export type Board = {
  id: string;
  title: string;
  selected: boolean;
};

export type Design = {
  id: string;
  title: string;
};

export type Tag = {
  id: string;
  title: string;
  amount: number;
  selected: boolean;
};

export type TagGroup = {
  parent: Tag;
  child: Tag[];
};

export type Materials = {
  id: string;
  imgSrc: string;
  title: string;
  userFavorite: boolean;
  userContact: boolean;
  description: string;
};

export type Detail = {
  index: number;
  id: string;
  slideList: {
    type: string;
    id: string;
    src: string;
    poster?: string;
  }[];
  title: string;
  number?: string;
  tagList: {
    id: string;
    name: string;
  }[];
  useRuler: boolean;
  userFavorite: boolean;
  userContact: boolean;
  itemNo: string;
  description: string;
  finish: string;
  width: string;
  density: string;
  warpAndWeft: string;
  weight: string;
  refprc: string;
  profit?: string;
  exchangeRate?: string;
  cost?: string;
  remarks?: string;
  tabShow: boolean;
  download: string;
  print: string;
  qrCode: string;
  category: string;
};

export type Display = {
  colorMap: {
    // 顏色Map => 從材質點進來，LB看到展示在該模型上，有很多材質代表色的選擇
    [id: string]: { id: string; src: string }[]; // [id: (key)模型名稱]: { id: BASE or 代表色碼, src: 對應連結 }
  };
  modelGroupMap: {
    // 模型群組Map
    [id: string]: {
      // [id: (key)模型種類]
      title: string; // 模型種類
      modelList: {
        // 該種類下的模型列表陣列
        id: string; // 模型名稱
        src: string; // 對應的snapshot => "https://d1lisc9qcq4rqd.cloudfront.net/Base/Model/Group10/K4_wallet/K4_wallet_snapshot.png"
      }[];
    };
  };
  modelPathMap: {
    // 模型路徑Map
    [group: string]: {
      // [group: (key)模型種類]
      [model: string]: { url: string; done: boolean }; // [model: (key)模型名稱]: { url: fbx連結 => "https://d1lisc9qcq4rqd.cloudfront.net/Base/Model/Group10/K4_wallet/K4_wallet_fbx.fbx?v2", done: 是否讀取完成 }
    };
  };
  materialPathMap: {
    // 材質路徑Map
    [model: string]: {
      // [model: (key)模型名稱]
      baseColor: { [key: string]: { url: string; done: boolean } }; // 代表色Map => [key: (key)代表色]: { url: 合成後的圖 => "https://d1lisc9qcq4rqd.cloudfront.net/Merge/Group10/K4_wallet/YL-M021-K/YL-M021-K_KHS-R1832K_BaseColor_12-0824TCX.png", done: 是否讀取完成 }
      normal: { url: string; done: boolean };
      roughness: { url: string; done: boolean };
      metalness: { url: string; done: boolean };
    };
  };
};

export type DisplayProModelTexture = {
  baseColor: THREE.Texture;
  metallic: THREE.Texture;
  normal: THREE.Texture;
  roughness: THREE.Texture;
  opacity: THREE.Texture;
};

export type DisplayProAccessoryTexture = {
  baseColor: THREE.Texture;
  metallic: THREE.Texture;
  normal: THREE.Texture;
  roughness: THREE.Texture;
};

export type DisplayProAccessoryArrType = {
  [key: string]: {
    // key => mpaid_acid 因為acid有可能在不同位置上重複出現 要限定位置id+配件id
    accessoryName: string;
    acid: string;
    isSetting: number;
    mpasid: number;
    //
    fbx: string;
    snapshot: string;
    //
    baseColor: string;
    metallic: string;
    normal: string;
    roughness: string;
    accessoryTexture: DisplayProAccessoryTexture;
    scale_x: string;
    scale_y: string;
    scale_z: string;
    rotation_x: string;
    rotation_y: string;
    rotation_z: string;
    sort: string;
  };
};

export type DisplayProPageInfoType = {
  // info
  model: {
    [mgid: string]: {
      groupName: string;
      mgid: string;
      modelArr: {
        [mpid: string]: {
          isElement: number;
          modelName: string;
          mpid: string;
          tilling: string;
          elementArr: {
            [mpeid: string]: {
              mpeid: string;
              name_system: string;
              name_user: string;
              type: string;
              // 單色 需要切換元件時保留當初的顏色 才會有act
              isSetting: boolean;
              mcid: string;
              mccid: string;
              // 單色追加 顏色名稱
              colorName?: string;
              // 雙色
              isDoubleSetting: boolean;
              doubleColorIndex: number;
              // 雙色追加 色碼名稱與檔案路徑
              doubleColorName?: string;
              doubleColorPath?: string;
              // 紀錄當下color
              color: string;
            };
          };
          model: {
            baseColor: string;
            fbx: string;
            metallic: string;
            normal: string;
            roughness: string;
            opacity: string;
            snapshot: string;
            data: {};
            modelTexture: DisplayProModelTexture;
            modelAccessoryArr: {
              [mpaid: string]: {
                mpaid: string;
                modelAccessoryName: string;
                position_x: string;
                position_y: string;
                position_z: string;
                scale_x: string;
                scale_y: string;
                scale_z: string;
                rotation_x: string;
                rotation_y: string;
                rotation_z: string;
                sort: string;
                agid: string;
                accessoryArr: DisplayProAccessoryArrType;
                // 需要切換配件時保留當初的設定
                isSetting: boolean;
                acid: string;
                uuid: string;
              };
            };
          };
        };
      };
    };
  };
  material: {
    base: string;
    mtl: string;
    nrm: string;
    rough: string;
    baseGrayscale: string;
    colorArr: string[];
    alpha: string;
  };
  selectMpgid: string;
  selectMpid: string;
  selectMpaid: string;
  selectAcid: string;
  submitAccessory: {
    [mpaidKey: string]: {
      mpaid: string;
      acid: string;
      uuid: string;
    };
  }; // 送出的配件
  submitColor?: {
    [mpidKey: string]: {
      elementArr: {};
    };
  }; // 切換模型時 紀錄當前的顏色配置
  selectMpeid: string;

  isColorList: boolean;
  selectMcid: string;
  selectMccid: string;
  selectAccessoryPage: number;
  accessoryLastPage: number;

  // reset use
  resetAcid: string;
  resetMpeid: string;
  resetMcid: string;
  resetMccid: string;
  resetMpaid: string;
  defaultColorListName: string;

  // 材質雙色
  selectDoubleColorIndex: number;

  materialTexture: {
    baseColor: THREE.Texture;
    metallic: THREE.Texture;
    normal: THREE.Texture;
    roughness: THREE.Texture;
    alpha: THREE.Texture;
    baseGrayscale: THREE.Texture;
    colorArr: THREE.Texture[];
  };
};

export type ModelProGroupMapType = {
  [mpgidKey: string]: {
    mpgid: string;
    groupName: string;
    modelMap: {
      [mccidKey: string]: {
        mpid: string;
        modelName: string;
        snapshot: string;
      };
    };
  };
};

// ------------------------------ class ------------------------------

class MaterialsModel {
  private static sInstance: MaterialsModel;

  public static get instance(): MaterialsModel {
    if (!MaterialsModel.sInstance) {
      MaterialsModel.sInstance = new MaterialsModel();
    }

    return MaterialsModel.sInstance;
  }

  // ------------------------------ home ------------------------------

  private boardList: Board[] = [];
  private homeData: HomeData = {
    bannerList: [],
    materials: {
      themeTitle: "",
      downloadFilePath: "",
      list: {}
    }
  };

  // ------------------------------ banner ------------------------------

  private bannerNum = "";
  private bannerList: Banner[] = [];

  // ------------------------------ materials ------------------------------

  private pageShow = "";
  private pageHtml = "";
  private materialIdentity = "";
  private tagGroupList: TagGroup[] = [];
  private materialsList: Materials[] = [];
  private detail!: Detail;
  private displayMap: {
    [key: string]: Display;
  } = {};
  private error = 0;

  public async getHomeData(): Promise<void> {
    await Network.sendAPI("/index/getHomeInfo", "post")
      .then(response => {
        const rawData = response.data as { [key: string]: unknown };
        this.convertHomeData(rawData);
      })
      .catch(error => {
        Log.error(error);
      });
  }

  public async getBannerData(item?: object): Promise<void> {
    await Network.sendAPI("/index/getBannerInfo", "post", item)
      .then(response => {
        const rawData = response.data as { [key: string]: unknown };
        this.convertBannerData(rawData);
      })
      .catch(error => {
        Log.error(error);
      });
  }

  public async getDataByCondition(item?: object): Promise<void> {
    await Network.sendAPI("/materials/getMaterialInfo", "post", item)
      .then(response => {
        const rawData = response.data as { [key: string]: unknown };
        this.convertMaterialData(rawData);
      })
      .catch(error => {
        Log.error(error);
      });
  }

  public async getDetailByID(item: object): Promise<void> {
    await Network.sendAPI("/materials/getMaterial", "post", item)
      .then(response => {
        const rawData = response.data as { [key: string]: unknown };
        this.convertDetailData(rawData);
      })
      .catch(error => {
        Log.error(error);
      });
  }

  public async get3DDisplayPathByID(id: string, item: object): Promise<void> {
    await Network.sendAPI("/materials/getMaterial3D", "post", item)
      .then(response => {
        const rawData = response.data as { [key: string]: unknown };
        this.error = rawData.err as number;
        if (this.error === 2) {
          this.convertDisplayData(id, rawData);
        }
      })
      .catch(error => {
        Log.error(error);
      });
  }

  public async get3DProDisplayPathByID(id: string, item: object): Promise<any> {
    let list = {};
    await Network.sendAPI("/materials/getMaterial3DPro", "post", item)
      .then(response => {
        const rawData = response.data as { [key: string]: unknown };
        this.error = rawData.err as number;
        if (this.error === 2) {
          list = rawData.list as any;
        }
      })
      .catch(error => {
        Log.error(error);
      });
    return list;
  }

  public async get3DProDisplayPathByModel(
    id: string,
    item: object
  ): Promise<any> {
    let list = {};
    await Network.sendAPI("/materials/getMaterial3DProByModel", "post", item)
      .then(response => {
        const rawData = response.data as { [key: string]: unknown };
        this.error = rawData.err as number;
        if (this.error === 2) {
          list = rawData.list as any;
        }
      })
      .catch(error => {
        Log.error(error);
      });
    return list;
  }

  public async recordClickTimes(item: object): Promise<number> {
    let errNo = Constant.ErrorCode.NotResponse;

    await Network.sendAPI("/member/addRecord", "post", item)
      .then(response => {
        const data = response.data as { [key: string]: unknown };
        errNo = data.ERR_CODE as number;
      })
      .catch(error => {
        Log.error(error);
      });

    return errNo;
  }

  // ------------------------------ home ------------------------------

  private convertHomeData(rawData: { [key: string]: unknown }): void {
    const bannerList = rawData.bannerList as Banner[];
    const materialList = rawData.materialList as {
      maid: string;
      name: string;
      pic: string;
      url: string;
    }[];
    const themeList = rawData.themeList as {
      themeName: string;
      pdf: string;
      list: {
        maid: string;
        name: string;
        pic: string;
        url: string;
      }[];
    };
    const memberCollectList = rawData.memberCollectList as {
      mcid: string;
      mid: string;
      name: string;
      note: string;
    }[];
    const favoriteList = rawData.favoriteList as string[];
    const requestList = rawData.requestList as string[];

    const materials: HomeMaterials = {
      themeTitle: themeList.themeName ? themeList.themeName : "",
      downloadFilePath: themeList.pdf ? themeList.pdf : "",
      list: {
        new: [],
        theme: []
      }
    };
    materialList.forEach(material => {
      const item = {
        id: material.maid,
        src: material.pic,
        url: material.url,
        title: material.name,
        userFavorite: favoriteList.find(item => item === material.maid)
          ? true
          : false,
        userContact: requestList.find(item => item === material.maid)
          ? true
          : false
      };
      materials.list.new.push(item);
    });

    if (themeList.list) {
      themeList.list.forEach(theme => {
        const item = {
          id: theme.maid,
          src: theme.pic,
          url: theme.url,
          title: theme.name,
          userFavorite: favoriteList.find(item => item === theme.maid)
            ? true
            : false,
          userContact: requestList.find(item => item === theme.maid)
            ? true
            : false
        };
        materials.list.theme.push(item);
      });
    }

    this.homeData.bannerList = bannerList;
    this.homeData.materials = materials;

    this.boardList = [];
    memberCollectList.forEach(memberCollect => {
      const item = {
        id: memberCollect.mcid,
        title: memberCollect.name,
        selected: false
      };
      this.boardList.push(item);
    });
  }

  public getBannerList(): Banner[] {
    return this.homeData.bannerList;
  }

  public getMaterials(): HomeMaterials {
    return this.homeData.materials;
  }

  public getBoardList(): Board[] {
    return this.boardList;
  }

  // ------------------------------ banner ------------------------------

  private convertBannerData(rawData: { [key: string]: unknown }): void {
    this.bannerNum = rawData.bannerNum as string;
    const bannerList = rawData.bannerList as Banner[];
    this.bannerList = bannerList;
  }

  public getBannerNum(): string {
    return this.bannerNum;
  }

  public getBannerPage(): Banner[] {
    return this.bannerList;
  }

  // ------------------------------ materials ------------------------------

  private convertMaterialData(rawData: { [key: string]: unknown }): void {
    const materialIdentity = rawData.memberIdentity as string;
    const groupList = rawData.groupList as {
      [key: string]: {
        groupName: string;
        tagList: {
          [key: string]: {
            materialUseThisTagCount: string;
            tagName: string;
            tsid: string;
          };
        };
      };
    };
    const materialList = rawData.materialList as {
      maid: string;
      name: string;
      pic: { [key: string]: string };
      description: string;
      show: string;
    }[];
    const memberCollectList = rawData.memberCollectList as {
      mcid: string;
      mid: string;
      name: string;
      note: string;
    }[];
    const favoriteList = rawData.favoriteList as string[];
    const requestList = rawData.requestList as string[];

    this.materialIdentity = materialIdentity;
    this.tagGroupList = [];
    Object.keys(groupList).forEach(groupKey => {
      const raw = groupList[groupKey];
      const item: TagGroup = {
        parent: {
          id: raw.groupName.toLowerCase(),
          title: raw.groupName,
          amount: 0,
          selected: false
        },
        child: []
      };
      const tagList = groupList[groupKey].tagList;
      Object.keys(tagList).forEach(tagKey => {
        const tag = tagList[tagKey];
        const childItem = {
          id: tag.tsid,
          title: tag.tagName,
          amount: parseInt(tag.materialUseThisTagCount),
          selected: false
        };
        item.child.push(childItem);
      });
      this.tagGroupList.push(item);
    });

    this.materialsList = [];
    materialList.forEach(material => {
      const item = {
        id: material.maid,
        imgSrc: material.pic["01"],
        title: material.name,
        userFavorite: favoriteList.find(item => item === material.maid)
          ? true
          : false,
        userContact: requestList.find(item => item === material.maid)
          ? true
          : false,
        description: material.description,
        show: material.show
      };
      this.materialsList.push(item);
    });

    this.boardList = [];
    memberCollectList.forEach(memberCollect => {
      const item = {
        id: memberCollect.mcid,
        title: memberCollect.name,
        selected: false
      };
      this.boardList.push(item);
    });

    this.pageShow = rawData.pageShow as string;
    this.pageHtml = rawData.pageHtml as string;
  }

  public getMaterialIdentity(): string {
    return this.materialIdentity;
  }

  public getTagGroupList(): TagGroup[] {
    return this.tagGroupList;
  }

  public getMaterialsList(): Materials[] {
    return this.materialsList;
  }

  public getPageShow(): string {
    return this.pageShow;
  }

  public getPageHtml(): string {
    return this.pageHtml;
  }

  private convertDetailData(rawData: { [key: string]: unknown }): void {
    const rawDetail = rawData.material as {
      // from api -> database
      pic: { [key: string]: string };
      active: string;
      cost: string;
      density: string;
      description: string;
      description_percent: string;
      finish: string;
      item_no: string;
      maid: string;
      materialTag: string;
      materialTagName: string;
      name: string;
      no: string;
      note: string;
      profit: string;
      ref: string;
      sample: string;
      show: string;
      weight: string;
      width: string;
      download: string;
      print: string;
      url: string;
      isFavorite: string;
      isContact: string;
      exchangeRate: string;
      category: string;
    };
    const tagList = rawDetail.materialTag.split(",");
    const tagNameList = rawDetail.materialTagName.split(",");
    const finalTagList: {
      id: string;
      name: string;
    }[] = [];
    tagList.forEach((tag, index) => {
      const item = {
        id: tag,
        name: tagNameList[index]
      };
      finalTagList.push(item);
    });

    const slideList: {
      type: string;
      id: string;
      src: string;
      poster?: string;
    }[] = [];

    // 20220121 => materialInfo 2D 0405 -04-05
    if (rawDetail.pic["-04"]) {
      const itemTwo = {
        type: "img",
        id: `${rawDetail.maid}_04`,
        src: rawDetail.pic["-04"]
      };
      slideList.push(itemTwo);
    }

    if (rawDetail.pic["-05"]) {
      const itemOne = {
        type: "img",
        id: `${rawDetail.maid}_05`,
        src: rawDetail.pic["-05"]
      };
      slideList.push(itemOne);
    }

    if (rawDetail.pic["_video"]) {
      const itemVideo = {
        type: "video",
        id: `${rawDetail.maid}_video`,
        src: rawDetail.pic["_video"],
        poster: rawDetail.pic["_videoCover"]
      };
      slideList.push(itemVideo);
    }

    const isFavorite: boolean = rawDetail.isFavorite === "1" ? true : false;
    const isContact: boolean = rawDetail.isContact === "1" ? true : false;
    console.log(rawDetail);
    this.detail = {
      index: 0,
      id: rawDetail.maid,
      slideList: slideList,
      title: rawDetail.name,
      number: rawDetail.no,
      tagList: finalTagList,
      useRuler: false,
      userFavorite: isFavorite,
      userContact: isContact,
      itemNo: rawDetail.item_no,
      description: rawDetail.description,
      finish: rawDetail.finish,
      width: rawDetail.width,
      density: rawDetail.density,
      warpAndWeft: rawDetail.description_percent,
      weight: rawDetail.weight,
      refprc: `USD ${rawDetail.ref}`,
      profit: rawDetail.profit,
      exchangeRate: rawDetail.exchangeRate,
      cost: rawDetail.cost,
      remarks: rawDetail.note,
      tabShow: rawDetail.show === "2", // show: {1: 2d, 2: 2d+3d}
      download: rawDetail.download,
      print: rawDetail.print,
      qrCode: rawDetail.url,
      category: rawDetail.category
    };
  }

  public getDetail(): Detail {
    return this.detail;
  }

  private convertDisplayData(
    id: string,
    rawData: { [key: string]: unknown }
  ): void {
    this.displayMap[id] = {
      colorMap: {},
      modelGroupMap: {},
      modelPathMap: {},
      materialPathMap: {}
    };

    const rawDisplay = rawData.list as {
      [group: string]: {
        [material: string]: {
          material: {
            base: { [key: string]: string };
            color: { [key: string]: string };
          };
          merge: {
            base: { [key: string]: string };
            color: { [key: string]: string };
          };
          model: {
            fbx: string;
            snapshot: string;
          };
        };
      };
    };
    const rawColorMap = rawData.smallColorList as {
      [colorCode: string]: string;
    };

    const colorList: { id: string; src: string }[] = [];
    Object.keys(rawColorMap).forEach(colorCode => {
      const colorItem = {
        id: colorCode,
        src: rawColorMap[colorCode]
      };
      colorList.push(colorItem);
    });

    const modelGroupMap: {
      [id: string]: {
        title: string;
        modelList: {
          id: string;
          src: string;
        }[];
      };
    } = {};
    const modelPathMap: {
      [group: string]: {
        [model: string]: {
          url: string;
          done: boolean;
        };
      };
    } = {};
    Object.keys(rawDisplay).forEach(group => {
      const colorMap: {
        [id: string]: {
          id: string;
          src: string;
        }[];
      } = {};

      const materialPathMap: {
        [model: string]: {
          baseColor: {
            [key: string]: {
              url: string;
              done: boolean;
            };
          };
          normal: {
            url: string;
            done: boolean;
          };
          roughness: {
            url: string;
            done: boolean;
          };
          metalness: {
            url: string;
            done: boolean;
          };
        };
      } = {};

      if (!modelGroupMap[group]) {
        modelGroupMap[group] = {
          title: group,
          modelList: []
        };
      }

      Object.keys(rawDisplay[group]).forEach(material => {
        const item = rawDisplay[group][material];

        if (!modelPathMap[group]) {
          modelPathMap[group] = {};
        }
        if (!colorMap[material]) {
          colorMap[material] = [];
        }
        colorMap[material] = colorList;

        const modelPath = {
          url: item.model.fbx,
          done: false
        };
        modelPathMap[group][material] = modelPath;

        const modelGroupItem = {
          id: material,
          src: item.model.snapshot
        };
        modelGroupMap[group].modelList.push(modelGroupItem);

        if (!materialPathMap[material]) {
          materialPathMap[material] = {
            baseColor: {},
            normal: {
              url: item.merge.base.NRM,
              done: false
            },
            roughness: {
              url: item.merge.base.ROUGH,
              done: false
            },
            metalness: {
              url: item.merge.base.MTL,
              done: false
            }
          };
        }
        if (item.merge.color) {
          Object.keys(item.merge.color).forEach(mergeColor => {
            materialPathMap[material].baseColor[mergeColor] = {
              url: item.merge.color[mergeColor],
              done: false
            };
          });
        }
        if (rawColorMap["BASE"]) {
          materialPathMap[material].baseColor["BASE"] = {
            url: item.merge.base.BASE,
            done: false
          };
        }

        this.displayMap[id].colorMap = {
          ...this.displayMap[id].colorMap,
          ...colorMap
        };
        this.displayMap[id].modelGroupMap = {
          ...this.displayMap[id].modelGroupMap,
          ...modelGroupMap
        };
        this.displayMap[id].materialPathMap = {
          ...this.displayMap[id].materialPathMap,
          ...materialPathMap
        };
      });
      this.displayMap[id].modelPathMap = modelPathMap;
    });
  }

  public getError(): number {
    return this.error;
  }

  public getDisplay(id: string): Display {
    return this.displayMap[id];
  }

  // 取得模型種類清單
  public async getModelProGroupMap(): Promise<[number, ModelProGroupMapType]> {
    let errNo = ErrorCode.NotResponse;
    let modelProGroupMap: ModelProGroupMapType = {};

    await Network.sendAPI("/materials/getModelProGroupMap", "post")
      .then(response => {
        const data = response.data as { [key: string]: unknown };
        console.log(data);
        errNo = data.ERR_CODE as number;
        if (errNo === ErrorCode.Success) {
          modelProGroupMap = data.map as ModelProGroupMapType;
        }
      })
      .catch(error => {
        Log.error(error);
      });

    return [errNo, modelProGroupMap];
  }

  public async getMemberBoardList(item: object): Promise<Board[]> {
    const boardList: Board[] = [];
    await Network.sendAPI("/materials/getMemberBoardList", "post", item)
      .then(response => {
        const rawData = response.data as { [key: string]: unknown };
        const memberCollectList = rawData.memberCollectList as {
          mcid: string;
          mid: string;
          name: string;
          note: string;
        }[];

        memberCollectList.forEach(memberCollect => {
          const item = {
            id: memberCollect.mcid,
            title: memberCollect.name,
            selected: false
          };
          boardList.push(item);
        });
      })
      .catch(error => {
        Log.error(error);
      });

    return boardList;
  }

  public async getMemberDesignList(item: object): Promise<Design[]> {
    const designList: Design[] = [];
    await Network.sendAPI("/materials/getMemberDesignList", "post", item)
      .then(response => {
        const rawData = response.data as { [key: string]: unknown };
        const memberDesignList = rawData.memberDesignList as {
          mdlid: string;
          mid: string;
          name: string;
          description: string;
        }[];

        memberDesignList.forEach(memberDesign => {
          const item = {
            id: memberDesign.mdlid,
            title: memberDesign.name,
          };
          designList.push(item);
        });
      })
      .catch(error => {
        Log.error(error);
      });

    return designList;
  }

  public async submitConfiguration(item: FormData): Promise<number> {
    let errNo = ErrorCode.NotResponse;

    console.log(item);
    await Network.uploadFile("/materials/submitConfiguration", item)
      .then(response => {
        console.log(response);
        const data = response.data as { [key: string]: unknown };
        errNo = data.ERR_CODE as number;
      })
      .catch(error => {
        Log.error(error);
      });

    return errNo;
  }

  public async submitConfigurationAndDownload(item: FormData): Promise<number> {
    let errNo = ErrorCode.NotResponse;

    await Network.uploadFileAndDownload("/materials/submitConfiguration", item)
      .then(response => {
        // console.log(response);
        errNo = ErrorCode.Success;
      })
      .catch(error => {
        Log.error(error);
      });

    return errNo;
  }

  public async downloadPdf(item: FormData): Promise<number> {
    let errNo = ErrorCode.NotResponse;

    await Network.uploadFileAndDownload("/materials/downloadPdf", item)
      .then(response => {
        // console.log(response);
        errNo = ErrorCode.Success;
      })
      .catch(error => {
        Log.error(error);
      });

    return errNo;
  }

  public async addQRCodeInfoAndCreateImg(item: object): Promise<[string, string]> {
    let img = "";
    let marker = "";
    await Network.sendAPI("/materials/addQRCodeInfoAndCreateImg", "post", item)
      .then(response => {
        const rawData = response.data as { [key: string]: unknown };
        img = rawData.img as string;
        marker = rawData.marker as string;
      })
      .catch(error => {
        Log.error(error);
      });

    return [img, marker];
  }
}

const materialsModel = MaterialsModel.instance;

export { materialsModel as MaterialsModel };
